/**
 * Where a row dropped after a sibling lands (`planMoveAfter`): the
 * position(s) `RowAccessor.moveAfter` writes so the row shows right after
 * `after`, even when the siblings around the drop share one position.
 *
 * Positions are `Rational`s in display order (`Rational.compare`), and a new
 * one is the mediant of its neighbours. The mediant of two EQUAL positions
 * is equal to them, so a drop between tied rows cannot land with one write.
 * Rows tie often: the server creates rows at one default position. Then the
 * tied run around the drop is re-spaced: every row of the run, and the
 * moved row, gets a distinct position between the run's untied
 * neighbours, in the order the user sees with the drop applied.
 */

import { Rational } from "@tento-core/rational";
import type { Option } from "@tento-lona/sheets";

/** One sibling as shown: its id and position (`null`: none stored). */
export type PlacedSibling<Id> = { readonly id: Id; readonly position: Option<Rational> };

/** One write of a plan: `id` to `position`. */
export type PlannedMove<Id> = { readonly id: Id; readonly position: Rational };

/**
 * The writes that put `moved` right after `after` among `siblings`
 * (display order, `moved` itself excluded; `after` `null`: first). One
 * write when the neighbours are strictly ordered; otherwise one per row
 * of the tied run, plus the moved row. Throws when `after` is not a
 * sibling.
 */
export function planMoveAfter<Id>(
  siblings: readonly PlacedSibling<Id>[],
  moved: Id,
  after: Option<Id>,
): PlannedMove<Id>[] {
  const i = after == null ? -1 : siblings.findIndex((s) => s.id === after);
  if (after != null && i < 0) throw new Error("planMoveAfter: `after` is not a sibling of the moved row's new parent");
  const before = i >= 0 ? siblings[i]?.position ?? null : null;
  const next = siblings[i + 1]?.position ?? null;
  const single = between(before, next);
  if (single != null) return [{ id: moved, position: single }];
  // A tie (or an unstored position) at the drop: widen to the run whose
  // neighbours are not strictly ordered, bounded by strictly ordered rows.
  const strict = (j: number): boolean => {
    const a = siblings[j]?.position ?? null;
    const b = siblings[j + 1]?.position ?? null;
    return a != null && b != null && Rational.compare(a, b) < 0;
  };
  let lo = Math.max(i, 0);
  while (lo > 0 && !strict(lo - 1)) lo--;
  let hi = Math.min(i + 1, siblings.length - 1);
  while (hi < siblings.length - 1 && !strict(hi)) hi++;
  const order: Id[] = [];
  for (let j = lo; j <= hi; j++) {
    const sibling = siblings[j];
    if (sibling == null) continue;
    if (j === i + 1 || (i < 0 && j === lo)) {
      if (!order.includes(moved)) order.push(moved);
    }
    order.push(sibling.id);
  }
  if (!order.includes(moved)) order.push(moved);
  const upper = lo > 0 ? siblings[lo - 1]?.position ?? null : null;
  const lower = hi + 1 < siblings.length ? siblings[hi + 1]?.position ?? null : null;
  const plan: PlannedMove<Id>[] = [];
  let previous: Option<Rational> = upper;
  for (const id of order) {
    const position: Rational = previous == null && lower == null
      ? Rational.first()
      : Rational.midpoint(previous, lower);
    plan.push({ id, position });
    previous = position;
  }
  return plan;
}

/** A position strictly between `before` and `next` (display order), or `null` when there is none in one step. */
function between(before: Option<Rational>, next: Option<Rational>): Option<Rational> {
  if (before == null && next == null) return Rational.first();
  const candidate = Rational.midpoint(before, next);
  if (before != null && Rational.compare(before, candidate) >= 0) return null;
  if (next != null && Rational.compare(candidate, next) >= 0) return null;
  return candidate;
}
